import type { RecordingId } from "@repo/app-types/entities/recording";
import type { GradingId } from "@repo/app-types/entities/teammatePerformanceSummary";
import { fetchChatCompletion } from "@repo/ui/chat/api";
import type {
  ClientAssistantChatMessage,
  ClientUserChatMessage,
} from "@repo/ui/chat/types";
import * as Sentry from "@sentry/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  apiBaseUrl,
  getApiAuthorizationHeaderValue,
} from "../../api/shared.js";
import { useAppSelector } from "../../hooks/hook.js";
import { selectTeammate } from "../teammate/teammateSlice.js";

type ClientLiveCoachingMessage =
  | ClientUserChatMessage
  | ClientAssistantChatMessage;

interface UseLiveCoachingCopilotParams {
  /** Grading whose call is being coached. */
  gradingId: GradingId;
  /** ID of the recording being played back as a "live" call. */
  recordingId: RecordingId | undefined;
  /** Current playback time, used to slice the transcript on the server. */
  callTimeSeconds: number;
}

interface UseLiveCoachingCopilotResult {
  visibleMessages: readonly ClientLiveCoachingMessage[];
  isLoading: boolean;
  composerValue: string;
  setComposerValue: (value: string) => void;
  onMessageSend: (message: string) => void;
}

interface PendingRequest {
  userMessage: ClientUserChatMessage;
  callTimeMillis: number;
}

/**
 * Live coaching copilot. Chat history is strictly ephemeral — messages live in
 * component state only and are never persisted server-side. Each completion
 * request includes the full conversation plus the current call time so the
 * server can slice the transcript to "what's been said so far".
 */
export function useLiveCoachingCopilot(
  params: UseLiveCoachingCopilotParams,
): UseLiveCoachingCopilotResult {
  const { gradingId, recordingId, callTimeSeconds } = params;

  const currentTeammate = useAppSelector(selectTeammate);

  const [messages, setMessages] = useState<ClientLiveCoachingMessage[]>([]);
  const [pendingRequest, setPendingRequest] = useState<PendingRequest | null>(
    null,
  );
  const [composerValue, setComposerValue] = useState("");

  // Always read the latest call time when the request fires, but don't
  // re-trigger the effect every time playback advances.
  const callTimeSecondsRef = useRef(callTimeSeconds);
  useEffect(() => {
    callTimeSecondsRef.current = callTimeSeconds;
  }, [callTimeSeconds]);

  const handleMessageSend = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) {
      return;
    }

    const userMessage: ClientUserChatMessage = {
      id: uuidv4(),
      role: "user",
      createdAt: new Date().toISOString(),
      content: trimmed,
      isSaving: true,
    };

    setMessages((prev) => [...prev, userMessage]);
    setPendingRequest({
      userMessage,
      callTimeMillis: Math.round(callTimeSecondsRef.current * 1000),
    });
    setComposerValue("");
  };

  const upsertMessage = (message: ClientLiveCoachingMessage) => {
    setMessages((prev) => {
      const index = prev.findIndex((m) => m.id === message.id);
      if (index === -1) {
        return [...prev, message];
      }
      const next = [...prev];
      next[index] = message;
      return next;
    });
  };

  // Fire chat completion when a request is queued.
  useEffect(() => {
    if (!currentTeammate || !recordingId || !pendingRequest) {
      return;
    }

    const abortController = new AbortController();

    const run = async () => {
      const authorizationHeader = await getApiAuthorizationHeaderValue();
      const url = new URL(
        `/teammates/${currentTeammate.id}/copilot/live-coaching`,
        apiBaseUrl,
      );

      // Build the message history as the server expects it. We snapshot
      // `messages` at request time so the user message is included.
      const historyForServer = [...messages, pendingRequest.userMessage]
        .filter(
          (m): m is ClientUserChatMessage | ClientAssistantChatMessage =>
            m.role === "user" || m.role === "assistant",
        )
        .map((m) => ({
          id: m.id,
          role: m.role,
          content: m.content,
        }));

      const completionStream = fetchChatCompletion(url, {
        headers: {
          Authorization: authorizationHeader,
          "x-alpharun-commit-sha": __COMMIT_SHA__,
        },
        body: {
          messages: historyForServer,
          chat_details: {
            type: "live_coaching",
            grading_id: gradingId,
            recording_id: recordingId,
            call_time_millis: pendingRequest.callTimeMillis,
          },
        },
        signal: abortController.signal,
      });

      // Mark the user message as saved (it's already in local state).
      upsertMessage({ ...pendingRequest.userMessage, isSaving: false });

      for await (const message of completionStream) {
        if (message.role === "assistant") {
          upsertMessage(message);
        }
      }
    };

    run().then(
      () => {
        setPendingRequest((current) =>
          current === pendingRequest ? null : current,
        );
      },
      (error) => {
        if (error.name === "AbortError") {
          return;
        }

        upsertMessage({
          ...pendingRequest.userMessage,
          isSaving: false,
          isError: true,
        });

        setPendingRequest((current) =>
          current === pendingRequest ? null : current,
        );

        Sentry.captureException(error);
      },
    );

    return () => {
      abortController.abort();
    };
    // We intentionally only re-run when the queued request changes — `messages`
    // is read at firing time via closure but should not cause re-fires.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTeammate, pendingRequest, gradingId, recordingId]);

  const visibleMessages = useMemo(
    () => messages.filter((m) => !!m.content || m.role === "user"),
    [messages],
  );

  const lastMessage = messages.at(-1);
  const isLastMessageStreaming =
    lastMessage?.role === "assistant" &&
    lastMessage.isLoading &&
    !!lastMessage.content;
  const isLoading = pendingRequest !== null && !isLastMessageStreaming;

  return {
    visibleMessages,
    isLoading,
    composerValue,
    setComposerValue,
    onMessageSend: handleMessageSend,
  };
}
